import { isBefore } from "date-fns";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetFacilityProfile } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";

/**
 * Workplaces that first went live on or after this date do not see the classic
 * calendar. Remove this constant (and all references) once the classic calendar
 * feature is fully deprecated.
 */
const CLASSIC_CALENDAR_CUTOFF_DATE = new Date("2026-07-24T00:00:00.000Z");

/**
 * Returns whether the given date is strictly before the classic calendar cutoff.
 * Reused for both the workplace's account-creation date and its first-shift date.
 * Fail-closed: an undefined date is treated as not-before-cutoff (i.e. "new").
 */
export function isBeforeClassicCalendarCutoff(date: string | undefined): boolean {
  if (!isDefined(date)) {
    return false;
  }

  return isBefore(new Date(date), CLASSIC_CALENDAR_CUTOFF_DATE);
}

/**
 * Resolves whether a workplace should see the classic calendar or hide it. The
 * purpose is to prepare the deprecation of the calendar view for all workplace
 * users, so we don't want new workplaces to get used to it.
 *
 * The calendar is hidden for all workplaces created after the the cutoff date,
 * or that started posting shifts after the cutoff date (to catch workplaces
 * that were created before but did not start using the platform yet)
 */
export function useShouldShowClassicCalendar(params: {
  isWorkplace: boolean;
  workplaceUserId: string | undefined;
  workplaceCreatedAt: string | undefined;
  hasAdminPrivileges: boolean;
  isClassicCalendarException: boolean;
}): boolean {
  const {
    isWorkplace,
    workplaceUserId,
    workplaceCreatedAt,
    hasAdminPrivileges,
    isClassicCalendarException,
  } = params;

  const isCreatedBeforeCutoff = isBeforeClassicCalendarCutoff(workplaceCreatedAt);

  // Only fetch first shift posted date, when it influences the decision
  const shouldConsultFirstShift =
    isCreatedBeforeCutoff && !hasAdminPrivileges && !isClassicCalendarException;

  const { data: firstShiftProfile, isSuccess: isFirstShiftKnown } = useGetFacilityProfile(
    workplaceUserId ?? "",
    "firstShiftCreatedAt",
    { enabled: isWorkplace && isDefined(workplaceUserId) && shouldConsultFirstShift }
  );

  if (hasAdminPrivileges || isClassicCalendarException) {
    return true;
  }

  if (!isCreatedBeforeCutoff) {
    return false;
  }

  // show the old calendar, if the fetch did not succeed
  return isFirstShiftKnown
    ? isBeforeClassicCalendarCutoff(firstShiftProfile?.firstShiftCreatedAt ?? undefined)
    : true;
}
